import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router';
import { api, qs, type ReportColumnKind, type ReportData, type ReportListItem } from '../api';
import { Button, Card, Empty, ErrorMessage, PageHeader, Select, Spinner, money, number, unitMoney } from '../ui';

const GROUPS: ReportListItem['group'][] = ['Stock', 'Sales', 'Buying', 'Pricing', 'Housekeeping'];

/** The list of reports, by what they are about. */
export function Reports() {
  const list = useQuery({ queryKey: ['reports'], queryFn: () => api<{ reports: ReportListItem[] }>('/api/v1/reports').then((r) => r.reports) });
  return (
    <>
      <PageHeader title="Reports" subtitle="Questions about your stock and your buying. Any report can be saved as a spreadsheet." />
      {list.isPending && <Spinner />}
      {list.error && <ErrorMessage error={list.error} />}
      <div className="grid gap-4 lg:grid-cols-2">
        {list.data &&
          GROUPS.map((g) => (
            <Card key={g} title={g}>
              <ul className="space-y-3">
                {list.data.filter((r) => r.group === g).map((r) => (
                  <li key={r.key}>
                    <Link to={`/reports/${r.key}`} className="font-medium hover:underline">
                      {r.title}
                    </Link>
                    <p className="text-sm text-slate-600">{r.about}</p>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        <Card title="Pricing lists">
          <ul className="space-y-3">
            <li>
              <Link to="/pricing" className="font-medium hover:underline">
                Losing money, thin margins and unpriced lots
              </Link>
              <p className="text-sm text-slate-600">The lots where the price needs attention, with suggestions you can apply.</p>
            </li>
          </ul>
        </Card>
      </div>
    </>
  );
}

function cell(kind: ReportColumnKind, v: string | number | null) {
  if (v === null || v === undefined || v === '') return '—';
  switch (kind) {
    case 'int':
      return number(Number(v));
    case 'money':
      return money(v);
    case 'unit':
      return unitMoney(v);
    case 'pct':
      return `${Number(v) > 0 ? '+' : ''}${v}%`;
    case 'months':
      return `${v}`;
    default:
      return String(v);
  }
}

const csvCell = (v: string | number | null) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n\r]/.test(s) || /^[=+\-@]/.test(s) ? `"${(/^[=+\-@]/.test(s) ? `'${s}` : s).replace(/"/g, '""')}"` : s;
};

/** Saves what is on screen as a CSV. Text that starts like a formula is quoted so a spreadsheet will not run it. */
function download(r: ReportData) {
  const head = r.columns.map((c) => csvCell(c.label)).join(',');
  const body = r.rows.map((row) => r.columns.map((c) => csvCell(row[c.key] ?? null)).join(','));
  const blob = new Blob([[head, ...body].join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${r.key}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/** One report, as a table. */
export function ReportView() {
  const { key } = useParams();
  const [params, setParams] = useSearchParams();
  const by = params.get('by');
  const report = useQuery({
    queryKey: ['report', key, by],
    queryFn: () => api<ReportData>(`/api/v1/reports/${key}${qs({ by })}`),
  });
  const r = report.data;
  const totals = r?.columns.some((c) => c.kind === 'money' || c.kind === 'int') && r.rows.length > 1 && r.total === r.rows.length;

  return (
    <>
      <PageHeader
        title={r?.title ?? 'Report'}
        subtitle={
          <>
            <Link to="/reports" className="hover:underline">
              Reports
            </Link>{' '}
            / {r?.title ?? key}
          </>
        }
        actions={
          r && r.rows.length > 0 ? (
            <Button variant="secondary" onClick={() => download(r)}>
              Save as CSV
            </Button>
          ) : undefined
        }
      />
      {report.isPending && <Spinner />}
      {report.error && <ErrorMessage error={report.error} />}
      {r && (
        <Card>
          <p className="mb-3 text-sm text-slate-600">{r.about}</p>
          {r.options?.map((o) => (
            <div key={o.name} className="mb-3 flex items-center gap-2 text-sm">
              <label htmlFor={`opt-${o.name}`}>{o.label}</label>
              <Select id={`opt-${o.name}`} className="w-48" value={o.value} onChange={(e) => setParams({ [o.name]: e.target.value })}>
                {o.choices.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
            </div>
          ))}
          {r.rows.length === 0 ? (
            <Empty>Nothing to show.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="data w-full min-w-[40rem] text-sm">
                <thead>
                  <tr>
                    {r.columns.map((c) => (
                      <th key={c.key} className={c.kind === 'text' || c.kind === 'date' ? undefined : 'text-right'}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {r.rows.map((row, i) => (
                    <tr key={i}>
                      {r.columns.map((c, j) => (
                        <td key={c.key} className={c.kind === 'text' || c.kind === 'date' ? undefined : 'text-right tabular-nums'}>
                          {j === 0 && typeof row.href === 'string' ? (
                            <Link to={row.href} className="font-medium hover:underline">
                              {cell(c.kind, row[c.key] ?? null)}
                            </Link>
                          ) : (
                            cell(c.kind, row[c.key] ?? null)
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {totals && (
                  <tfoot>
                    <tr className="font-semibold">
                      {r.columns.map((c, j) => (
                        <td key={c.key} className={c.kind === 'text' || c.kind === 'date' ? undefined : 'text-right tabular-nums'}>
                          {j === 0
                            ? 'Total'
                            : (c.kind === 'money' || c.kind === 'int') && !/^(each|avg|margin|price)/.test(c.key)
                              ? cell(c.kind, r.rows.reduce((s, row) => s + (Number(row[c.key]) || 0), 0))
                              : ''}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
          {r.total > r.rows.length && <p className="mt-2 text-xs text-slate-500">Showing the first {number(r.rows.length)} of {number(r.total)}.</p>}
          {r.note && <p className="mt-2 text-xs text-slate-500">{r.note}</p>}
        </Card>
      )}
    </>
  );
}
